'use client'

import { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { useAuthStore } from '@/lib/stores/auth-store'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  CheckCircle2,
  AlertCircle,
  Package,
  Search,
  BarChart2,
  Circle,
  Filter
} from 'lucide-react'
import Link from 'next/link'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useLocale } from 'next-intl'
import { Button } from '@/components/ui/button'
import { RoadmapFilterSidebar } from './roadmap-filter-sidebar'

interface RoadmapViewProps {
  projectId?: string
  showProjectSelector?: boolean
}

interface Project {
  id: number
  name: string
  identifier: string
  description?: string
  status: number
}

interface Version {
  id: number
  name: string
  description?: string
  due_date?: string
  status: 'open' | 'locked' | 'closed'
  sharing: string
  created_on: string
  updated_on: string
  project?: {
    id: number
    name: string
  }
}

interface Issue {
  id: number
  subject: string
  status: {
    id: number
    name: string
    is_closed: boolean
  }
  tracker: {
    id: number
    name: string
  }
  priority: {
    id: number
    name: string
  }
  project: {
    id: number
    name: string
  }
  done_ratio: number
}

export function RoadmapView({ projectId: initialProjectId, showProjectSelector = false }: RoadmapViewProps) {
  const t = useTranslations('roadmapPage')
  const locale = useLocale()
  const isRTL = locale === 'ar' || locale === 'ur'
  const { session } = useAuthStore()
  
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<string>(initialProjectId || 'all')
  const [projectSearch, setProjectSearch] = useState('')
  const [versions, setVersions] = useState<Version[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [issuesData, setIssuesData] = useState<Record<number, {
    total: number
    closed: number
    open: number
    issues: Issue[]
  }>>({})
  
  // Filter states
  const [trackers, setTrackers] = useState<Array<{ id: number; name: string }>>([])
  const [selectedTrackers, setSelectedTrackers] = useState<Set<number>>(new Set())
  const [allVersions, setAllVersions] = useState<Array<{ id: number; name: string }>>([])
  const [selectedVersions, setSelectedVersions] = useState<Set<number>>(new Set())
  const [showCompletedVersions, setShowCompletedVersions] = useState(false)
  const [subprojects, setSubprojects] = useState<Project[]>([])
  const [selectedSubprojects, setSelectedSubprojects] = useState<Set<number>>(new Set())
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false)

  const getAuthHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (session?.apiKey) {
      headers['X-Redmine-API-Key'] = session.apiKey
    }
    return headers
  }

  useEffect(() => {
    if (showProjectSelector) {
      fetchProjects()
      fetchTrackers()
    }
  }, [showProjectSelector])

  useEffect(() => {
    if (selectedProjectId && selectedProjectId !== 'all') {
      fetchVersions(selectedProjectId)
    } else {
      setVersions([])
      setIssuesData({})
      setAllVersions([])
      setSubprojects([])
      setSelectedSubprojects(new Set())
    }
  }, [selectedProjectId])

  const fetchProjects = async () => {
    try {
      const authHeaders = getAuthHeaders()
      const response = await fetch('/api/projects?limit=1000', {
        headers: authHeaders
      })

      if (!response.ok) throw new Error('Failed to fetch projects')

      const data = await response.json()
      setProjects(data.projects || [])
    } catch (err) {
      console.error('Failed to fetch projects:', err)
    }
  }

  const fetchTrackers = async () => {
    try {
      const authHeaders = getAuthHeaders()
      const response = await fetch('/api/trackers', {
        headers: authHeaders
      })

      if (!response.ok) return

      const data = await response.json()
      const fetchedTrackers = data.trackers || []
      setTrackers(fetchedTrackers)
      // Initially select all trackers
      setSelectedTrackers(new Set(fetchedTrackers.map((t: any) => t.id)))
    } catch (err) {
      console.error('Failed to fetch trackers:', err)
    }
  }

  const fetchSubprojects = async (projectId: string) => {
    try {
      const authHeaders = getAuthHeaders()
      const response = await fetch(`/api/projects/${projectId}?include=children`, {
        headers: authHeaders
      })

      if (!response.ok) return

      const data = await response.json()
      const children = data.project?.children || []
      setSubprojects(children)
    } catch (err) {
      console.error('Failed to fetch subprojects:', err)
    }
  }

  const fetchVersions = async (projectId: string) => {
    setLoading(true)
    setError(null)

    try {
      const authHeaders = getAuthHeaders()
      
      // Fetch main project versions
      const response = await fetch(`/api/projects/${projectId}/versions`, {
        headers: authHeaders
      })

      if (!response.ok) throw new Error('Failed to fetch versions')

      const data = await response.json()
      let fetchedVersions = data.versions || []
      
      // Add project info to main project versions
      const projectIdNum = parseInt(projectId)
      fetchedVersions = fetchedVersions.map((v: Version) => ({
        ...v,
        project: v.project || { id: projectIdNum, name: '' }
      }))
      
      // Fetch subprojects to get their versions too
      // We need to fetch all projects and filter for children (same approach as project details page)
      const allProjectsResponse = await fetch(`/api/projects?limit=1000`, {
        headers: authHeaders
      })
      
      
      
      if (allProjectsResponse.ok) {
        const allProjectsData = await allProjectsResponse.json()
        const allProjects = allProjectsData.projects || []
        
  // Filter for direct children only (projects where parent.id matches current project)
  const children = allProjects.filter((p: any) => p.parent?.id === parseInt(projectId))
        
        
        // Store subprojects for the filter sidebar
        setSubprojects(children)
        
        // Fetch versions for each subproject
        for (const child of children) {
          try {
            const subResponse = await fetch(`/api/projects/${child.id}/versions`, {
              headers: authHeaders
            })
            
            if (subResponse.ok) {
              const subData = await subResponse.json()
              const subVersions = (subData.versions || []).map((v: Version) => ({
                ...v,
                project: { id: child.id, name: child.name }
              }))
              fetchedVersions = [...fetchedVersions, ...subVersions]
            }
          } catch (err) {
            console.error(`Failed to fetch versions for subproject ${child.id}:`, err)
          }
        }
      }

      // Sort like Redmine: Open versions first (by closest due date), then closed/locked
      const sortedVersions = fetchedVersions.sort((a: Version, b: Version) => {
        const aIsOpen = a.status === 'open'
        const bIsOpen = b.status === 'open'
        
        if (aIsOpen !== bIsOpen) {
          return aIsOpen ? -1 : 1
        }
        
        if (a.due_date && b.due_date) {
          return new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
        }
        
        if (a.due_date) return -1
        if (b.due_date) return 1
        
        return a.name.localeCompare(b.name)
      })

      setVersions(sortedVersions)

      // Store versions for filter sidebar
      const versionList = sortedVersions.map((v: Version) => ({ id: v.id, name: v.name }))
      setAllVersions(versionList)
      // Initially select all versions
      setSelectedVersions(new Set(versionList.map((v: { id: number; name: string }) => v.id)))

      // Fetch issues for all versions
      sortedVersions.forEach((version: Version) => {
        fetchVersionIssues(version.id)
      })

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roadmap')
    } finally {
      setLoading(false)
    }
  }

  const fetchVersionIssues = async (versionId: number) => {
    if (issuesData[versionId]) return

    try {
      const authHeaders = getAuthHeaders()
      const response = await fetch(
        `/api/issues?fixed_version_id=${versionId}&limit=1000&status_id=*`,
        { headers: authHeaders }
      )

      if (!response.ok) return

      const data = await response.json()
      const issues: Issue[] = data.issues || []

      const closed = issues.filter(issue => issue.status.is_closed).length
      const total = issues.length
      const open = total - closed

      setIssuesData(prev => ({
        ...prev,
        [versionId]: {
          total,
          closed,
          open,
          issues
        }
      }))
    } catch (err) {
      console.error(`Failed to fetch issues for version ${versionId}:`, err)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <Badge variant="default" className="uppercase text-xs font-bold">Open</Badge>
      case 'locked':
        return <Badge variant="secondary" className="uppercase text-xs font-bold">Locked</Badge>
      case 'closed':
        return <Badge variant="outline" className="uppercase text-xs font-bold">Closed</Badge>
      default:
        return <Badge variant="outline" className="uppercase text-xs font-bold">{status}</Badge>
    }
  }

  const getDueDateStatus = (dueDate: string | undefined, status: string) => {
    if (!dueDate) return null
    
    const due = new Date(dueDate)
    const now = new Date()
    const diffTime = due.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (status === 'closed') {
      return null
    }

    if (diffDays < 0) {
      return (
        <span className="text-red-600 font-medium">
          {Math.abs(diffDays)} day{Math.abs(diffDays) !== 1 ? 's' : ''} late ({new Date(dueDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })})
        </span>
      )
    } else if (diffDays === 0) {
      return <span className="text-amber-600 font-medium">Due today</span>
    } else {
      return (
        <span className="text-muted-foreground">
          Due in {diffDays} day{diffDays !== 1 ? 's' : ''} ({new Date(dueDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })})
        </span>
      )
    }
  }

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
    project.identifier.toLowerCase().includes(projectSearch.toLowerCase())
  )

  // Toggle tracker filter
  const toggleTracker = (trackerId: number) => {
    const newSelected = new Set(selectedTrackers)
    if (newSelected.has(trackerId)) {
      newSelected.delete(trackerId)
    } else {
      newSelected.add(trackerId)
    }
    setSelectedTrackers(newSelected)
  }

  // Toggle version filter
  const toggleVersion = (versionId: number) => {
    const newSelected = new Set(selectedVersions)
    if (newSelected.has(versionId)) {
      newSelected.delete(versionId)
    } else {
      newSelected.add(versionId)
    }
    setSelectedVersions(newSelected)
  }

  // Toggle subproject filter
  const toggleSubproject = (projectId: number) => {
    const newSelected = new Set(selectedSubprojects)
    if (newSelected.has(projectId)) {
      newSelected.delete(projectId)
    } else {
      newSelected.add(projectId)
    }
    setSelectedSubprojects(newSelected)
  }

  // Filter versions based on selected versions, completed status, and subprojects
  const filteredVersions = versions.filter(version => {
    // Filter by selected versions
    if (selectedVersions.size > 0 && selectedVersions.size < allVersions.length) {
      if (!selectedVersions.has(version.id)) return false
    }
    
    // Filter by completed status
    if (!showCompletedVersions && (version.status === 'closed' || version.status === 'locked')) {
      return false
    }
    
    // Filter by subproject
    // If some (but not all) subprojects are selected, only show:
    // 1. Versions from selected subprojects, OR
    // 2. Versions from the main project (not a subproject)
    if (selectedSubprojects.size > 0 && selectedSubprojects.size < subprojects.length) {
      const mainProjectId = parseInt(selectedProjectId)
      const versionProjectId = version.project?.id
      
      // Show if it's from the main project OR from a selected subproject
      const isMainProject = versionProjectId === mainProjectId
      const isSelectedSubproject = versionProjectId && selectedSubprojects.has(versionProjectId)
      
      if (!isMainProject && !isSelectedSubproject) {
        return false
      }
    }
    
    return true
  })

  // Calculate active filters count
  const activeFiltersCount = 
    (selectedTrackers.size < trackers.length && selectedTrackers.size > 0 ? selectedTrackers.size : 0) +
    (selectedVersions.size < allVersions.length && selectedVersions.size > 0 ? selectedVersions.size : 0) +
    (showCompletedVersions ? 1 : 0) +
    (selectedSubprojects.size > 0 ? selectedSubprojects.size : 0)

  // Filter issues by selected trackers
  const getFilteredIssues = (versionId: number) => {
    const versionData = issuesData[versionId]
    if (!versionData) return null

    if (selectedTrackers.size === 0 || selectedTrackers.size === trackers.length) {
      return versionData
    }

    const filteredIssues = versionData.issues.filter(issue => 
      selectedTrackers.has(issue.tracker.id)
    )

    const closed = filteredIssues.filter(issue => issue.status.is_closed).length
    const total = filteredIssues.length
    const open = total - closed

    return {
      total,
      closed,
      open,
      issues: filteredIssues
    }
  }

  const VersionCard = ({ version }: { version: Version }) => {
    const versionIssues = getFilteredIssues(version.id)
    const progress = versionIssues && versionIssues.total > 0 
      ? Math.round((versionIssues.closed / versionIssues.total) * 100) 
      : 0

    return (
      <div className="py-10 border-b last:border-b-0 first:pt-0">
        {/* Version Header - Clean & Minimal */}
        <div className="mb-6">
          {/* Title Row */}
          <div className="flex items-center justify-between mb-4">
            <div className="space-y-2">
              <div className="flex items-baseline gap-3 flex-wrap">
                <h2 className="text-xl font-semibold text-foreground tracking-tight">
                  {version.name}
                </h2>
                {version.project && version.project.name && (
                  <Badge variant="outline" className="text-xs font-normal">
                    {version.project.name}
                  </Badge>
                )}
                {version.due_date && (
                  <span className="text-sm text-muted-foreground font-normal">
                    {new Date(version.due_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-3 flex-wrap">
                {getStatusBadge(version.status)}
                {getDueDateStatus(version.due_date, version.status)}
              </div>
            </div>

            {/* Progress - Prominent */}
            {versionIssues && (
              <div className="text-right">
                <div className="text-5xl font-bold text-primary leading-none mb-1">
                  {progress}%
                </div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">
                  Complete
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          {version.description && (
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              {version.description}
            </p>
          )}

          {/* Stats Row */}
          {versionIssues && (
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground">{versionIssues.total}</span>
                <span className="text-muted-foreground">
                  {versionIssues.total === 1 ? 'issue' : 'issues'}
                </span>
              </div>
              <div className="h-4 w-px bg-border" />
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <span className="text-muted-foreground">
                  {versionIssues.closed} closed
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Circle className="h-4 w-4 text-primary" />
                <span className="text-muted-foreground">
                  {versionIssues.open} open
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Related Issues */}
        {versionIssues && versionIssues.issues.length > 0 && (
          <div>
            <Card>
              <CardContent className="p-0">
                <div className="divide-y">
                  {versionIssues.issues.map((issue, index) => (
                    <Link
                      key={issue.id}
                      href={`/issues/${issue.id}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors group"
                    >
                      {/* Status Icon */}
                      <div className="flex-shrink-0">
                        {issue.status.is_closed ? (
                          <CheckCircle2 className="h-5 w-5 text-primary" />
                        ) : (
                          <Circle className="h-5 w-5 text-primary" />
                        )}
                      </div>

                      {/* Issue Content */}
                      <div className="flex-1 min-w-0 grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-4 items-center">
                        {/* Project & Tracker - 3 cols */}
                        <div className="lg:col-span-3 flex items-center gap-2 flex-wrap">
                          <Badge variant="secondary" className="text-xs font-medium whitespace-nowrap">
                            {issue.project.name}
                          </Badge>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {issue.tracker.name}
                          </span>
                        </div>

                        {/* Issue ID - 1 col */}
                        <div className="lg:col-span-1">
                          <span className="text-sm font-semibold text-primary group-hover:text-primary/80">
                            #{issue.id}
                          </span>
                        </div>

                        {/* Subject - 6 cols */}
                        <div className="lg:col-span-6">
                          <span className="text-sm text-foreground group-hover:text-primary line-clamp-1">
                            {issue.subject}
                          </span>
                        </div>

                        {/* Status & Progress - 2 cols */}
                        <div className="lg:col-span-2 flex items-center gap-3 justify-end">
                          <Badge 
                            variant={issue.status.is_closed ? "outline" : "secondary"}
                            className={cn(
                              "text-xs whitespace-nowrap",
                              issue.status.is_closed && "text-muted-foreground"
                            )}
                          >
                            {issue.status.name}
                          </Badge>
                          <span className="text-xs font-medium text-muted-foreground min-w-[3rem] text-right">
                            {issue.done_ratio}%
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {versionIssues && versionIssues.issues.length === 0 && (
          <Card>
            <CardContent className="p-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted/50 mb-3">
                  <Package className="h-6 w-6 text-muted-foreground/50" />
                </div>
                <p className="text-sm text-muted-foreground">No issues in this version</p>
              </div>
            </CardContent>
          </Card>
        )}

        {!versionIssues && (
          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    {/* Status Icon Skeleton */}
                    <div className="flex-shrink-0">
                      <Skeleton className="h-5 w-5 rounded-full" />
                    </div>

                    {/* Issue Content Skeleton */}
                    <div className="flex-1 min-w-0 grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-4 items-center">
                      {/* Project & Tracker - 3 cols */}
                      <div className="lg:col-span-3 flex items-center gap-2 flex-wrap">
                        <Skeleton className="h-5 w-20" />
                        <Skeleton className="h-4 w-16" />
                      </div>

                      {/* Issue ID - 1 col */}
                      <div className="lg:col-span-1">
                        <Skeleton className="h-5 w-12" />
                      </div>

                      {/* Subject - 6 cols */}
                      <div className="lg:col-span-6">
                        <Skeleton className="h-5 w-full" />
                      </div>

                      {/* Status & Progress - 2 cols */}
                      <div className="lg:col-span-2 flex items-center gap-3 justify-end">
                        <Skeleton className="h-5 w-16" />
                        <Skeleton className="h-4 w-12" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Project Selector Header */}
      {showProjectSelector && (
        <div className="mb-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pb-6 border-b">
            {/* Left: Title */}
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold text-foreground tracking-tight">
                {t('title') || 'Roadmap'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('subtitle') || 'Track project versions and milestones'}
              </p>
            </div>

            {/* Right: Project Selector */}
            <div className="flex gap-2">
              <Select
                value={selectedProjectId}
                onValueChange={setSelectedProjectId}
              >
                <SelectTrigger className="w-[300px] h-10 border-muted-foreground/20">
                  <SelectValue placeholder={t('selectProject') || 'Select a project'} />
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className={cn('absolute top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground', isRTL ? 'right-3' : 'left-3')} />
                      <Input
                        placeholder="Search projects..."
                        value={projectSearch}
                        onChange={(e) => setProjectSearch(e.target.value)}
                        className={cn("h-9", isRTL ? "pr-9" : "pl-9")}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <SelectItem value="all">
                    <span className="font-medium">All Projects</span>
                  </SelectItem>
                  {filteredProjects.map((project) => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      <span className="truncate">{project.name}</span>
                    </SelectItem>
                  ))}
                  {filteredProjects.length === 0 && (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No projects found
                    </div>
                  )}
                </SelectContent>
              </Select>
              
              {/* Filter Button */}
              {selectedProjectId !== 'all' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsFilterSidebarOpen(true)}
                  className="h-10 px-3 relative"
                >
                  <Filter className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
                  Filters
                  {activeFiltersCount > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
                    >
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filter Sidebar */}
      <RoadmapFilterSidebar
        isOpen={isFilterSidebarOpen}
        onClose={() => setIsFilterSidebarOpen(false)}
        trackers={trackers}
        selectedTrackers={selectedTrackers}
        onTrackerToggle={toggleTracker}
        versions={allVersions}
        selectedVersions={selectedVersions}
        onVersionToggle={toggleVersion}
        showCompletedVersions={showCompletedVersions}
        onShowCompletedChange={setShowCompletedVersions}
        subprojects={subprojects}
        selectedSubprojects={selectedSubprojects}
        onSubprojectToggle={toggleSubproject}
        onResetFilters={() => {
          setSelectedTrackers(new Set(trackers.map(t => t.id)))
          setSelectedVersions(new Set(allVersions.map(v => v.id)))
          setShowCompletedVersions(false)
          setSelectedSubprojects(new Set())
        }}
      />

      {/* Loading State */}
      {loading && (
        <div className="space-y-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-12 w-32" />
              <Skeleton className="h-20 w-full" />
            </div>
          ))}
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State - Select Project */}
      {!loading && !error && selectedProjectId === 'all' && (
        <div className="text-center py-20">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
            <BarChart2 className="h-8 w-8 text-muted-foreground/50" />
          </div>
          <h3 className="text-lg font-medium mb-2 text-foreground">Select a Project</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Choose a project from the dropdown above to view its roadmap and track milestone progress
          </p>
        </div>
      )}

      {/* Empty State - No Versions */}
      {!loading && !error && selectedProjectId !== 'all' && filteredVersions.length === 0 && (
        <div className="text-center py-20">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
            <Package className="h-8 w-8 text-muted-foreground/50" />
          </div>
          <h3 className="text-lg font-medium mb-2 text-foreground">No Versions Found</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {versions.length === 0 
              ? "This project doesn't have any versions or milestones yet"
              : "No versions match the current filters"}
          </p>
        </div>
      )}

      {/* Versions List - Simple like Redmine */}
      {!loading && !error && filteredVersions.length > 0 && (
        <div className="space-y-0">
          {filteredVersions.map(version => (
            <VersionCard key={`${version.project?.id || 0}-${version.id}`} version={version} />
          ))}
        </div>
      )}
    </div>
  )
}
